import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
  DOCUMENT,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, skip } from 'rxjs';
import { EmbedNavigationService } from '../../services/embed-navigation.service';
import { embedCanonicalPath } from '../../core/embed-canonical';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-embed-layout',
  templateUrl: './embed-layout.component.html',
  styleUrl: './embed-layout.component.scss',
  imports: [RouterOutlet],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class EmbedLayoutComponent implements OnInit, AfterViewInit {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);

  @ViewChild('layout') private layout?: ElementRef<HTMLElement>;
  @ViewChild('content') private content?: ElementRef<HTMLElement>;

  constructor() {
    // Created here so it sees the iframe's first navigation (see the service docs).
    inject(EmbedNavigationService);
  }

  ngOnInit(): void {
    // Opening a job from a long list happens far down the customer's page; bring
    // the iframe top into view like a page load would. Browsers that refuse to
    // scroll a cross-origin parent simply ignore this.
    // The layout is created during the first navigation, after its NavigationEnd
    // may already have fired, so apply the canonical once right away.
    this.setCanonical();
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.setCanonical());

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        skip(1),
        filter(e => /^\/embed\/jobs\/[^/?#]+/.test(e.urlAfterRedirects)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() =>
        this.layout?.nativeElement.scrollIntoView({ block: 'start' })
      );
  }

  ngAfterViewInit(): void {
    // In-iframe navigation and async data change the content height after `load`;
    // keep sending the same `{ height }` message so customers that resize the iframe
    // stay right. Observe the content wrapper: `#layout` is pinned to the iframe
    // viewport, so its own size never changes when the content does.
    const el = this.content?.nativeElement;
    if (!el) {
      return;
    }
    let lastHeight = -1;
    const observer = new ResizeObserver(([entry]) => {
      const height = Math.ceil(entry.borderBoxSize[0].blockSize);
      if (height === lastHeight) {
        return;
      }
      lastHeight = height;
      window.parent.postMessage({ height }, '*');
    });
    observer.observe(el);
    this.destroyRef.onDestroy(() => observer.disconnect());
  }

  // Job detail and apply form manage their own head tags; every other embed points
  // to the public page it mirrors (#359).
  private setCanonical(): void {
    const tree = this.router.parseUrl(this.router.url);
    const path = embedCanonicalPath(this.router.url, tree.queryParams);
    if (path === undefined) return;
    this.document.head
      .querySelectorAll('link[rel="canonical"]')
      .forEach(link => link.remove());
    const link = this.document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', `${environment.appUrl}${path}`);
    this.document.head.appendChild(link);
  }
}
